import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { T } from "react-translator-component";
import { isClosedGroup, isPostCreator } from "../../util/utils";
import dayjs from "../../util/dayjs";
import appServices from "../../services/app-services/appServices";
import EventPostDetailSeeMore from "../../components/Phase2/EventPostDetailSeeMore";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import GroupIconImage from "../../components/GreenCity/GroupIconImage";
import LikeDislikeButton from "../../components/LikeDislikeButton";
import MebukuIdUpgradeBanner from "../../components/MebukuIdUpgradeBanner";
import Thumbnail from "../../components/Phase2/Thumbnail";
import VotesModel from "../../components/Models/VotesModel";
import Separator from "../../components/Separator";
import PostCommentButton from "../../components/PostCommentButton";
import LoadingModal from "../../components/LoadingModal";
import { ErrorMessageModal } from "../../components/ErrorMessageModal";
import {
  colors,
  ImageList,
  ImageListItem,
  ListItem,
  Typography,
  CircularProgress,
  IconButton,
  Box,
  Stack,
} from "@mui/material";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import styled from "styled-components";
import { styles } from "../../pages/styles";
import { styles as oldstyles } from "../../pages/styles";
import { colors as farmColor } from "../../common/colors";

const ModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-end; /* Align items to the bottom */
  z-index: 9999;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  width: 100%;
  height: 85%;
  overflow-y: hidden;
  z-index: 9999;
`;

export default function EventPostDetails() {
  const [showVotesModal, setShowVotesModal] = useState<number>(0);
  const location = useLocation();
  const postId = location.state;

  const navigate = useNavigate();
  const groupInfo = useSelector((state: any) => state.gcGroup);
  const { user } = useSelector((state: any) => state.userAuth);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [post, setPost] = useState<any>();
  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const [showErrorMessageModal, setShowErrorMessageModal] = useState<boolean>(false);
  const [votedOptionId, setVotedOptionId] = useState<number | null>(post?.voted_info?.my_votes[0] || null);

  const getSinglePost = async () => {
    try {
      setIsLoading(true)
      const res = await appServices.getSinglePost(postId);
      setPost(res?.post);
    } catch (error: any) {
      console.error(error)
      setErrorMessage(error?.response?.data?.message ?? [T("api_unknown_error")])
      setShowErrorMessageModal(true)
    } finally {
      setIsLoading(false)
    }
  };

  const closeErrorMessageModal = () => {
    setShowErrorMessageModal(false)
    setErrorMessage([])
    navigate(-1)
  }

  useEffect(() => {
    if (!postId) return
    getSinglePost()
  }, [postId])

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const formatEventDate = (start_date: any, end_date: any) => {
    const startDate = dayjs(start_date);
    const endDate = dayjs(end_date);

    if (startDate.isSame(endDate, "day")) {
      // If start and end dates are the same
      return startDate.format("YYYY年M月D日(ddd)");
    } else {
      // If start and end dates are different
      return `${startDate.format("YYYY年M月D日(ddd)")}~${endDate.format(
        "M月D日(ddd)"
      )}`;
    }
  };

  const isUnableToLike = () => {
    // 自分の投稿にはいいね不可
    if (isPostCreator(post, user)) return true

    // 既に閉じられたグループの投稿にはいいね不可
    if (isClosedGroup(post?.member_group?.closed_at)) return true

    return false
  }
  return (
    <div
      style={{
        marginTop: "90px",
        borderBottomWidth: 4,
        overflow: "hidden",
        display: "block",
      }}
    >
      <LoadingModal visible={isLoading} />
      <GCBackButton onClick={() => navigate(-1)} />
      <ImageList style={{ display: "block" }} cols={1}>
        <ImageListItem
          sx={{
            backgroundColor: farmColor.farm_gray10,
            borderRadius: 2,
          }}
        >
          <ul>
            <ListItem
              className=""
              alignItems="flex-start"
              style={{
                ...styles.detailListItem,
                borderBottomStyle: "solid",
                borderTopStyle: "solid",
                paddingLeft: 0,
                justifyContent: "start",
                alignItems: "center",
              }}
            >
              <Box style={style.eventPostingBox}>
                <Typography style={{ fontWeight: "700" }}>
                  <GroupIconImage
                    groupId={groupInfo.id}
                    alt={"user"}
                    style={{
                      ...oldstyles.farmProfileIcon,
                      height: 50,
                      width: 50,
                      margin: 0,
                    }}
                  />
                  &nbsp;{groupInfo?.groupName}
                </Typography>
              </Box>
            </ListItem>
          </ul>
          <div>
            {post && <Thumbnail deleteItem={() => {}} item={post} type="posts" />}
          </div>
          <div style={{ justifyContent: "center" }}>
            {false ? (
              <CircularProgress
                style={{
                  marginTop: 10,
                  marginBottom: -8,
                  marginLeft: 8,
                  marginRight: 8,
                }}
                size={24}
              />
            ) : (
              <LikeDislikeButton
                disabled={isUnableToLike()}
                iconSize={"lg"}
                liketype={"post"}
                item={post}
              />
            )}
          </div>
          <Typography
            style={{
              marginLeft: 6,
              marginRight: 6,
              fontSize: 20,
              fontWeight: "600",
            }}
          >
            {post?.title}
          </Typography>

          <Typography sx={{ color: "#A2A2A2", fontSize: 12, marginLeft: 1 }}>
            {T("event_post_date")}
            {formatEventDate(
              post?.post_events[0]?.start_date,
              post?.post_events[0]?.end_date
            )}
          </Typography>

          <EventPostDetailSeeMore item={post} />
          <Separator color={colors.grey[400]} sx={{ marginTop: 2 }} />

          <Box sx={{ width: "100%" }}></Box>

          <Stack direction="row" spacing={0}>
            {post?.enable_comments && <PostCommentButton post={post} />}

            {post?.enable_participants && (
              <Box>
                <IconButton
                  onClick={() => setShowVotesModal(post?.id)}
                  aria-label="thumbs"
                >
                  <HowToVoteIcon />
                  <Typography>{T("vote")}</Typography>
                </IconButton>
              </Box>
            )}
          </Stack>

          {showVotesModal === post?.id && (
            <ModalBackground onClick={() => setShowVotesModal(0)}>
              <ModalContainer onClick={(e: any) => e.stopPropagation()}>
                <VotesModel
                  post={post}
                  votedOptionId={votedOptionId}
                  closeModal={() => setShowVotesModal(0)}
                  onChange={(votedOptionId) => setVotedOptionId(votedOptionId)}
                />
              </ModalContainer>
            </ModalBackground>
          )}
        </ImageListItem>
      </ImageList>

      <MebukuIdUpgradeBanner />

      <ErrorMessageModal
        open={showErrorMessageModal}
        messages={errorMessage}
        onClose={() => closeErrorMessageModal()}
      />
    </div>
  );
}

const style = {
  eventPostingBox: {
    display: "flex",
    gap: "15px",
    alignItmes: "left",
    paddingTop: "10px",
    paddingLeft: "10px",
  },
};
